import React, { useEffect, useState } from 'react';
import { Loader2, EyeOff, RotateCcw, Trash2, Flag } from 'lucide-react';
import api, { mediaUrl } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import { SocialSources } from '../../components/admin/SocialSources';

const AdminCommunity = () => {
  const { toast } = useToast();
  const [data, setData] = useState({ posts: [], comments: [] });
  const [loading, setLoading] = useState(true);

  const fetchAll = async () => {
    setLoading(true);
    try { const r = await api.get('/admin/community/reported'); setData(r.data || { posts: [], comments: [] }); }
    catch (e) { console.error('Failed to load community:', e); } finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const act = async (fn, msg) => { try { await fn(); toast({ title: msg }); fetchAll(); } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail }); } };

  const hidePost = (p) => act(() => api.post(`/admin/community/posts/${p.id}/hide`), 'Post hidden');
  const unhidePost = (p) => act(() => api.post(`/admin/community/posts/${p.id}/unhide`), 'Post restored');
  const delPost = (p) => { if (window.confirm('Permanently delete this post?')) act(() => api.delete(`/community/posts/${p.id}`), 'Post deleted'); };
  const hideComment = (c) => act(() => api.post(`/admin/community/comments/${c.id}/hide`), 'Comment hidden');
  const unhideComment = (c) => act(() => api.post(`/admin/community/comments/${c.id}/unhide`), 'Comment restored');
  const delComment = (c) => { if (window.confirm('Permanently delete this comment?')) act(() => api.delete(`/community/comments/${c.id}`), 'Comment deleted'); };

  const empty = data.posts.length === 0 && data.comments.length === 0;

  return (
    <div className="space-y-6">
      <div>
        <div className="eyebrow mb-2">Admin</div>
        <h1 className="font-serif-display text-4xl text-burgundy">Community <span className="font-italic-display text-rose-italic">Moderation</span></h1>
        <p className="mt-2 text-mahogany/80">Reported or auto-hidden posts and comments. Posts auto-hide after 3 reports pending your review.</p>
      </div>

      <SocialSources />
      <h2 className="font-serif-display text-3xl text-burgundy pt-4">Reported <span className="font-italic-display text-rose-italic">Content</span></h2>

      {loading ? <div className="flex items-center gap-2 text-mahogany"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        : empty ? <div className="p-8 text-center text-mahogany/60 bg-white/60 rounded-2xl border border-rose/40" data-testid="moderation-empty">Nothing needs review. 🌿</div>
        : (
          <div className="space-y-8">
            {data.posts.length > 0 && (
              <div>
                <h2 className="font-serif-display text-2xl text-burgundy mb-3">Posts ({data.posts.length})</h2>
                <ul className="space-y-3" data-testid="moderation-posts">
                  {data.posts.map((p) => (
                    <li key={p.id} className="p-4 rounded-2xl border border-rose/50 bg-white/70" data-testid={`mod-post-${p.id}`}>
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="text-sm text-burgundy font-medium">{p.author_name}</div>
                        <div className="flex items-center gap-2">
                          <span className="text-[11px] px-2 py-0.5 rounded-full bg-red-100 text-red-700 flex items-center gap-1"><Flag className="w-3 h-3" /> {p.report_count} report{p.report_count === 1 ? '' : 's'}</span>
                          {p.status === 'hidden' && <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Hidden</span>}
                        </div>
                      </div>
                      <div className="mt-2 text-sm text-burgundy/85 whitespace-pre-wrap break-words">{p.content}</div>
                      {p.image_url && <img src={mediaUrl(p.image_url)} alt="" className="mt-2 max-h-40 rounded-lg" />}
                      <div className="mt-3 flex flex-wrap gap-2">
                        {p.status === 'hidden'
                          ? <button onClick={() => unhidePost(p)} data-testid={`mod-unhide-post-${p.id}`} className="pill-btn-outline text-green-700 border-green-300 hover:bg-green-50 py-1.5 text-sm"><RotateCcw className="w-4 h-4" /> Restore</button>
                          : <button onClick={() => hidePost(p)} data-testid={`mod-hide-post-${p.id}`} className="pill-btn-outline text-amber-700 border-amber-300 hover:bg-amber-50 py-1.5 text-sm"><EyeOff className="w-4 h-4" /> Hide</button>}
                        <button onClick={() => delPost(p)} data-testid={`mod-delete-post-${p.id}`} className="pill-btn-outline text-red-700 border-red-300 hover:bg-red-50 py-1.5 text-sm"><Trash2 className="w-4 h-4" /> Delete</button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {data.comments.length > 0 && (
              <div>
                <h2 className="font-serif-display text-2xl text-burgundy mb-3">Comments ({data.comments.length})</h2>
                <ul className="space-y-3" data-testid="moderation-comments">
                  {data.comments.map((c) => (
                    <li key={c.id} className="p-4 rounded-2xl border border-rose/50 bg-white/70" data-testid={`mod-comment-${c.id}`}>
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="text-sm text-burgundy font-medium">{c.author_name}</div>
                        <div className="flex items-center gap-2">
                          <span className="text-[11px] px-2 py-0.5 rounded-full bg-red-100 text-red-700 flex items-center gap-1"><Flag className="w-3 h-3" /> {c.report_count}</span>
                          {c.status === 'hidden' && <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Hidden</span>}
                        </div>
                      </div>
                      <div className="mt-2 text-sm text-burgundy/85 whitespace-pre-wrap break-words">{c.content}</div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {c.status === 'hidden'
                          ? <button onClick={() => unhideComment(c)} className="pill-btn-outline text-green-700 border-green-300 hover:bg-green-50 py-1.5 text-sm"><RotateCcw className="w-4 h-4" /> Restore</button>
                          : <button onClick={() => hideComment(c)} className="pill-btn-outline text-amber-700 border-amber-300 hover:bg-amber-50 py-1.5 text-sm"><EyeOff className="w-4 h-4" /> Hide</button>}
                        <button onClick={() => delComment(c)} className="pill-btn-outline text-red-700 border-red-300 hover:bg-red-50 py-1.5 text-sm"><Trash2 className="w-4 h-4" /> Delete</button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
    </div>
  );
};

export default AdminCommunity;
